import { useState } from "react";
import { motion } from "framer-motion";

// deterministic pseudo-random (stable across re-renders, no Math.random)
function seededRandom(seed) {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

/**
 * Rest state: icons scattered in random order/rotation, clustered toward the bottom
 * — but clamped so they stay inside the card (overflow:hidden always, nothing escapes).
 * Hover: the SAME icon elements animate into an evenly-spaced row, then oscillate
 * left/right like a marquee. One set of nodes, two position targets — no crossfade.
 */
export default function SkillIconField({ items }) {
  const [hovered, setHovered] = useState(false);

  const scatter = items.map((_, i) => ({
    left: `${4 + seededRandom(i * 12.9898) * 80}%`,
    top: `${42 + seededRandom(i * 78.233) * 40}%`, // clamped: stays within the card
    rotate: (seededRandom(i * 37.719) - 0.5) * 30,
  }));

  const step = items.length > 1 ? 82 / (items.length - 1) : 0;
  const arrangedLeft = (i) => `${8 + i * step}%`;

  const marqueeDuration = Math.max(items.length * 1.4, 7);

  return (
    <div className="relative w-full h-full overflow-hidden">
      <motion.div
        className="absolute inset-0"
        animate={{ x: hovered ? ["0%", "-12%", "0%"] : "0%" }}
        transition={
          hovered
            ? { duration: marqueeDuration, ease: "linear", repeat: Infinity, repeatType: "loop" }
            : { duration: 0.35 }
        }
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {items.map((item, i) => (
          <motion.div
            key={item.id}
            className="absolute w-11 h-11 rounded-xl bg-white shadow-sm flex items-center justify-center text-neutral-400"
            style={{ zIndex: items.length - i }}
            animate={{
              left: hovered ? arrangedLeft(i) : scatter[i].left,
              top: hovered ? "50%" : scatter[i].top,
              rotate: hovered ? 0 : scatter[i].rotate,
              y: "-50%",
            }}
            transition={{ type: "spring", stiffness: 220, damping: 24 }}
            title={item.name}
          >
            <item.Icon size={20} />
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
}